import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  Sparkles, Plus, TrendingUp, Users, MousePointer, Trophy,
  Calendar, ChevronRight, Settings, Trash2, BarChart3, Loader2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminBoostPage() {
  const [campaigns, setCampaigns] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [campaignName, setCampaignName] = useState('');
  const [rewardTiers, setRewardTiers] = useState([
    { tier_name: 'Bronze', referrals_required: 1, reward_description: 'Promoter Badge', reward_type: 'badge' },
    { tier_name: 'Silver', referrals_required: 3, reward_description: 'VIP Network Access', reward_type: 'vip_access' },
    { tier_name: 'Gold', referrals_required: 5, reward_description: 'Free Event Pass', reward_type: 'prize' }
  ]);
  const [creating, setCreating] = useState(false);
  const [selectedAnalytics, setSelectedAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  useEffect(() => {
    fetchCampaigns();
    fetchEvents();
  }, []);

  const fetchCampaigns = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${BACKEND_URL}/api/admin/boost/campaigns`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCampaigns(response.data.campaigns);
    } catch (error) {
      toast.error('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  const fetchEvents = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${BACKEND_URL}/api/admin/events?limit=200`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEvents(response.data.events || []);
    } catch (error) {
      console.error('Failed to load events');
    }
  };

  const fetchAnalytics = async (eventId) => {
    setAnalyticsLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${BACKEND_URL}/api/admin/boost/analytics/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedAnalytics(response.data);
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const createCampaign = async () => {
    if (!selectedEventId || !campaignName) {
      toast.error('Please select an event and enter a campaign name');
      return;
    }

    setCreating(true);
    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(`${BACKEND_URL}/api/admin/boost/campaigns`, {
        event_id: selectedEventId,
        campaign_name: campaignName,
        reward_tiers: rewardTiers,
        is_active: true
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Campaign created successfully!');
      setShowCreateModal(false);
      setCampaignName('');
      setSelectedEventId('');
      fetchCampaigns();
    } catch (error) {
      toast.error('Failed to create campaign');
    } finally {
      setCreating(false);
    }
  };

  const deleteCampaign = async (campaignId) => {
    if (!window.confirm('Are you sure you want to deactivate this campaign?')) return;

    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`${BACKEND_URL}/api/admin/boost/campaigns/${campaignId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Campaign deactivated');
      fetchCampaigns();
    } catch (error) {
      toast.error('Failed to delete campaign');
    }
  };

  const cleanupTestData = async () => {
    if (!window.confirm('This will hide all test/demo promoter data from leaderboards. Continue?')) return;

    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post(`${BACKEND_URL}/api/admin/boost/cleanup-test-data`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Cleaned up ${response.data.referrals_hidden} test entries`);
      fetchCampaigns(); // Refresh to show updated stats
    } catch (error) {
      toast.error('Failed to cleanup test data');
    }
  };

  const updateRewardTier = (index, field, value) => {
    const updated = [...rewardTiers];
    updated[index][field] = field === 'referrals_required' ? parseInt(value) : value;
    setRewardTiers(updated);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-orange-500 to-amber-500 rounded-lg">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Event Boost</h1>
                <p className="text-sm text-gray-500">Referral marketing campaigns</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button 
                onClick={cleanupTestData}
                variant="outline"
                className="text-gray-600 hover:text-gray-800"
                data-testid="cleanup-test-data-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" /> Hide Test Data
              </Button>
              <Button 
                onClick={() => setShowCreateModal(true)}
                className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600"
              >
                <Plus className="w-4 h-4 mr-2" /> Create Campaign
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
          </div>
        ) : (
          <div className="grid gap-6">
            {campaigns.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
                <Sparkles className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No campaigns yet</h3>
                <p className="text-gray-500 mb-6">Create your first Boost campaign to start driving referrals</p>
                <Button onClick={() => setShowCreateModal(true)}>
                  <Plus className="w-4 h-4 mr-2" /> Create Campaign
                </Button>
              </div>
            ) : (
              campaigns.map((campaign) => (
                <div key={campaign.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <div className="p-6">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-lg font-semibold text-gray-900">{campaign.campaign_name}</h3>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            campaign.is_active 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            {campaign.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500">{campaign.event_name}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => fetchAnalytics(campaign.event_id)}
                        >
                          <BarChart3 className="w-4 h-4 mr-1" /> Analytics
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteCampaign(campaign.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-3 gap-4 mt-6">
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-center gap-2 text-gray-500 mb-1">
                          <Users className="w-4 h-4" />
                          <span className="text-sm">Promoters</span>
                        </div>
                        <p className="text-2xl font-bold text-gray-900">{campaign.stats?.total_promoters || 0}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-center gap-2 text-gray-500 mb-1">
                          <MousePointer className="w-4 h-4" />
                          <span className="text-sm">Total Clicks</span>
                        </div>
                        <p className="text-2xl font-bold text-gray-900">{campaign.stats?.total_clicks || 0}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-center gap-2 text-gray-500 mb-1">
                          <TrendingUp className="w-4 h-4" />
                          <span className="text-sm">Conversions</span>
                        </div>
                        <p className="text-2xl font-bold text-orange-600">{campaign.stats?.total_conversions || 0}</p>
                      </div>
                    </div>

                    {/* Reward Tiers */}
                    {campaign.reward_tiers && campaign.reward_tiers.length > 0 && (
                      <div className="mt-6 pt-6 border-t border-gray-100">
                        <h4 className="text-sm font-medium text-gray-700 mb-3">Reward Tiers</h4>
                        <div className="flex flex-wrap gap-2">
                          {campaign.reward_tiers.map((tier, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center gap-1 px-3 py-1 bg-amber-50 text-amber-700 rounded-full text-sm"
                            >
                              <Trophy className="w-3 h-3" />
                              {tier.tier_name}: {tier.referrals_required}+ referrals
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Create Campaign Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Boost Campaign</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Select Event</label>
              <select
                value={selectedEventId}
                onChange={(e) => setSelectedEventId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              >
                <option value="">Choose an event...</option>
                {events.map((event) => (
                  <option key={event.id} value={event.id}>{event.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Campaign Name</label>
              <Input
                placeholder="e.g., Launch Promotion"
                value={campaignName}
                onChange={(e) => setCampaignName(e.target.value)}
              />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mb-3 block">Reward Tiers</label>
              <div className="space-y-3">
                {rewardTiers.map((tier, index) => (
                  <div key={index} className="grid grid-cols-3 gap-2">
                    <Input
                      placeholder="Tier name"
                      value={tier.tier_name}
                      onChange={(e) => updateRewardTier(index, 'tier_name', e.target.value)}
                    />
                    <Input
                      type="number"
                      placeholder="# referrals"
                      value={tier.referrals_required}
                      onChange={(e) => updateRewardTier(index, 'referrals_required', e.target.value)}
                    />
                    <Input
                      placeholder="Reward"
                      value={tier.reward_description}
                      onChange={(e) => updateRewardTier(index, 'reward_description', e.target.value)}
                    />
                  </div>
                ))}
              </div>
            </div>

            <Button
              onClick={createCampaign}
              disabled={creating || !selectedEventId || !campaignName}
              className="w-full bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600"
            >
              {creating ? 'Creating...' : 'Create Campaign'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Analytics Modal */}
      <Dialog open={!!selectedAnalytics} onOpenChange={() => setSelectedAnalytics(null)}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Campaign Analytics</DialogTitle>
          </DialogHeader>
          {analyticsLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
            </div>
          ) : selectedAnalytics && (
            <div className="space-y-6 py-4">
              {/* Summary Stats */}
              <div className="grid grid-cols-4 gap-4">
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-gray-900">{selectedAnalytics.summary.total_promoters}</p>
                  <p className="text-xs text-gray-500">Promoters</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-gray-900">{selectedAnalytics.summary.total_clicks}</p>
                  <p className="text-xs text-gray-500">Clicks</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-orange-600">{selectedAnalytics.summary.total_conversions}</p>
                  <p className="text-xs text-gray-500">Conversions</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-green-600">{selectedAnalytics.summary.conversion_rate}%</p>
                  <p className="text-xs text-gray-500">Conv. Rate</p>
                </div>
              </div>

              {/* Top Promoters */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-3">Top Promoters</h4>
                <div className="space-y-2">
                  {selectedAnalytics.top_promoters.slice(0, 5).map((promoter, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <span className="w-6 h-6 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center text-sm font-medium">
                          {index + 1}
                        </span>
                        <span className="text-sm text-gray-700">{promoter.referrer_email}</span>
                      </div>
                      <div className="text-right">
                        <span className="font-semibold text-orange-600">{promoter.registrations}</span>
                        <span className="text-xs text-gray-500 ml-1">referrals</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Platform Breakdown */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-3">Click Sources</h4>
                <div className="grid grid-cols-3 gap-4">
                  {Object.entries(selectedAnalytics.platform_breakdown).map(([platform, count]) => (
                    <div key={platform} className="bg-gray-50 rounded-lg p-3 text-center">
                      <p className="text-lg font-semibold text-gray-900">{count}</p>
                      <p className="text-xs text-gray-500">{platform}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
